/**
 * 외부메일 계정 추가·수정 팝업 (migration 164, 2026-09-05 사용자 지시).
 *
 * 개인설정에 있던 「외부 메일 계정」 화면을 여기로 옮기면서, 그 화면이 붙들고 있던 사실을
 * 그대로 가져왔다:
 *
 * ★★★ **SMTP 는 보내는 규약이라 그것만 넣으면 받은편지함이 빈다.** 받기·보내기를 한 흐름에서
 *    함께 받고, 상태도 각각 보인다(「보내기만 연결됨」이 실제로 있을 수 있는 상태다).
 * ★★★ **포트가 보안연결을 정한다** — 465·993·995 는 붙자마자 TLS(암묵), 나머지(587·143·110)는
 *    평문으로 붙어 STARTTLS 로 올린다. 그래서 포트를 고치면 보안연결을 따라 바꾸되,
 *    **손으로 바꾼 선택은 지킨다**(예전에 추측 결과가 사용자 선택을 덮어써 8초 타임아웃이 났다).
 * ★★ **비밀번호는 서버가 돌려주지 않는다** — 화면도 갖고 있지 않는다. 비워 두면 「그대로 둔다」다.
 * ★  IMAP 도 이제 실제로 가져온다(2026-09-07) — 「아직 안 가져온다」던 안내를 걷어냈다.
 */

import { useCallback, useState } from 'react';
import { Button } from '../../components/shared/Button';
import { Checkbox } from '../../components/shared/Checkbox';
import { FormField } from '../../components/shared/FormField';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { PasswordInput } from '../../components/shared/Input/PasswordInput';
import { Select } from '../../components/shared/Select';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import {
  listServerFolders, probeMailAccount,
  type MailAccount, type MailAccountInput, type ProbeResult, type RecvProtocol, type ServerMailFolder,
} from '../../api/external-mail';
import type { MailFolderNode } from '../../api/mail';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 저장된 비밀번호 자리 (2026-11-02 사용자 지시 — 「비밀번호가 투명으로 보인다, 채워 달라」).
 * ★ 서버는 비밀번호를 돌려주지 않는다 — 그래서 **값이 아니라 자리표시**로 채운다.
 *   값으로 넣으면 저장할 때 이 점들이 비밀번호로 올라간다. 적기 시작하면 사라지고, 비워 두면
 *   지금처럼 「그대로 둔다」다.
 */
const SAVED_PASS_MASK = '\u2022'.repeat(10);

/**
 * 고를 수 있는 확인 주기 — 서버가 받는 범위(60~86400초) 안에서 뜻 있는 값만.
 * ★ 자유 입력으로 두면 「7초」 같은 값이 들어와 남의 서버를 두드린다. 고르기로 막는다.
 */
const INTERVAL_OPTIONS = [60, 180, 300, 900, 1800, 3600] as const;
const INTERVAL_LABEL: Record<number, MailI18nKey> = {
  60: 'mail.ext.iv.1m', 180: 'mail.ext.iv.3m', 300: 'mail.ext.iv.5m',
  900: 'mail.ext.iv.15m', 1800: 'mail.ext.iv.30m', 3600: 'mail.ext.iv.1h',
};

/**
 * 폴더 한 줄에 무엇을 적나.
 *
 * ★★ **통 수를 함께 적는다** — 빈 폴더를 고를 이유가 없고, Junk·Trash 를 안 고를 이유도
 *   그 수에 있다(실측: Junk 8 · Trash 19).
 * ★ 스팸함·휴지통은 **권하지 않는다고 그 줄에서 말한다** — 감추면 「왜 안 보이나」가 되고,
 *   아무 말 없이 두면 무심코 고른다.
 */
function folderLabel(f: ServerMailFolder): string {
  const count = f.messages !== null ? ` (${f.messages})` : '';
  const warn = f.specialUse === '\\Junk' || f.specialUse === '\\Trash'
    ? ` — ${tMail('mail.ext.f.folders_not_advised')}`
    : '';
  return `${f.name}${count}${warn}`;
}

/** 규약마다 기본 포트가 다르다 — IMAP 993 / POP3 995(둘 다 SSL). */
const DEFAULT_RECV_PORT: Record<RecvProtocol, number> = { imap: 993, pop3: 995 };

/** 붙자마자 TLS 로 감싸는 포트들(암묵 TLS). 나머지는 평문으로 붙어 STARTTLS 로 올린다. */
const IMPLICIT_TLS_PORTS = new Set([465, 993, 995]);

interface Form {
  email: string;
  recvProtocol: RecvProtocol;
  recvHost: string; recvPort: number; recvSecure: boolean; recvUser: string; recvPass: string;
  smtpHost: string; smtpPort: number; smtpSecure: boolean; smtpUser: string; smtpPass: string;
  /** 「받기 비밀번호와 같음」 — 저장할 때 한 번 베낀다(계속 묶어 두는 것이 아니다). */
  smtpSamePass: boolean;
  /** 가져올 IMAP 폴더 경로 (migration 169). 빈 배열이면 받은편지함만. */
  recvFolders: string[];
  /** Axi 에서 읽은 것을 서버에도 읽음으로 (migration 170). 기본 꺼짐. */
  syncReadToServer: boolean;
  /** 몇 초마다 확인하나 (migration 107). 서버는 60~86400 을 받는다. */
  syncIntervalSec: number;
  targetFolderId: string;
  deleteOriginal: boolean;
}

const EMPTY: Form = {
  email: '', recvProtocol: 'imap', recvHost: '', recvPort: 993, recvSecure: true,
  recvUser: '', recvPass: '', smtpHost: '', smtpPort: 587, smtpSecure: false,
  smtpUser: '', smtpPass: '', smtpSamePass: false, recvFolders: [], syncReadToServer: false, syncIntervalSec: 300,
  targetFolderId: '', deleteOriginal: false,
};

const formOf = (a: MailAccount): Form => ({
  email: a.email,
  recvProtocol: a.recvProtocol,
  recvHost: a.recvHost ?? '',
  recvPort: a.recvPort ?? DEFAULT_RECV_PORT[a.recvProtocol],
  recvSecure: a.recvSecure,
  recvUser: a.recvUser ?? '',
  recvPass: '',
  smtpHost: a.smtpHost ?? '',
  smtpPort: a.smtpPort ?? 587,
  smtpSecure: a.smtpSecure,
  smtpUser: a.smtpUser ?? '',
  smtpPass: '',
  // ★ 켠 채로 기억하지 않는다 — 저장할 때 한 번 베끼는 것이라, 다시 열었을 때 «묶여 있다»고
  //   읽히면 거짓말이 된다(그 사이 받기 비밀번호가 바뀌었을 수 있다).
  smtpSamePass: false,
  recvFolders: a.recvFolders ?? [],
  syncReadToServer: a.syncReadToServer === true,
  syncIntervalSec: a.syncIntervalSec ?? 300,
  targetFolderId: a.targetFolderId ?? '',
  deleteOriginal: a.deleteOriginal === true,
});

export interface MailExternalAccountModalProps {
  target?: MailAccount | null;
  folders: MailFolderNode[];
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (input: MailAccountInput) => void;
}

export function MailExternalAccountModal({
  target, folders, busy, error, onClose, onSubmit,
}: MailExternalAccountModalProps) {
  const [form, setForm] = useState<Form>(target ? formOf(target) : EMPTY);
  const [local, setLocal] = useState<string | null>(null);
  /* 연결확인 결과 (2026-11-02) — 아래 runProbe 머리말. */
  const [probe, setProbe] = useState<{ recv: ProbeResult | null; smtp: ProbeResult | null } | null>(null);
  const [probing, setProbing] = useState(false);
  /*
   * 가져올 폴더 (migration 169, 2026-09-07).
   *
   * ★ 목록은 **서버에 실제로 붙어 봐야** 안다 — 그래서 저장된 계정에만 물을 수 있고,
   *   팝업을 열 때가 아니라 **누를 때** 부른다(계정을 고칠 때마다 남의 서버에 붙지 않게).
   */
  const [serverFolders, setServerFolders] = useState<ServerMailFolder[] | null>(null);
  const [foldersBusy, setFoldersBusy] = useState(false);
  const [foldersError, setFoldersError] = useState<string | null>(null);

  const loadFolders = useCallback(async () => {
    if (!target) return;
    setFoldersBusy(true);
    setFoldersError(null);
    try {
      setServerFolders(await listServerFolders(target.id));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setFoldersError(e.response?.data?.message ?? (err instanceof Error ? err.message : 'failed'));
    } finally {
      setFoldersBusy(false);
    }
  }, [target]);

  const toggleFolder = useCallback((path: string, on: boolean) => {
    setForm((prev) => ({
      ...prev,
      recvFolders: on
        ? [...prev.recvFolders.filter((p) => p !== path), path]
        : prev.recvFolders.filter((p) => p !== path),
    }));
  }, []);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (local) setLocal(null);
    if (probe) setProbe(null);
  };

  /*
   * 연결확인 (2026-11-02 사용자 지시) — 저장하지 않고 **지금 칸의 값**으로 붙어 본다.
   * ★ 결과는 칸을 고치면 지운다 — 옛 값의 「연결됨」이 새 값 곁에 남으면 거짓말이 된다.
   */
  const runProbe = async () => {
    if (!form.recvHost.trim() || !form.recvUser.trim()) { setLocal(tMail('mail.ext.err_host')); return; }
    setLocal(null);
    setProbe(null);
    setProbing(true);
    try {
      setProbe(await probeMailAccount({
        ...(target ? { accountId: target.id } : {}),
        recvProtocol: form.recvProtocol,
        recvHost: form.recvHost.trim(),
        recvPort: form.recvPort || null,
        recvSecure: form.recvSecure,
        recvUser: form.recvUser.trim(),
        ...(form.recvPass ? { recvPass: form.recvPass } : {}),
        smtpHost: form.smtpHost.trim() || null,
        smtpPort: form.smtpPort || null,
        smtpSecure: form.smtpSecure,
        smtpUser: form.smtpUser.trim() || null,
        ...(form.smtpSamePass
          ? { smtpSamePass: true }
          : (form.smtpPass ? { smtpPass: form.smtpPass } : {})),
      }));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setLocal(e.response?.data?.message ?? tMail('mail.ext.probe_failed'));
    } finally {
      setProbing(false);
    }
  };
  const probeLine = (labelKey: MailI18nKey, r: ProbeResult | null) => (
    <p className={r === null ? 'wc-set__hint' : r.ok ? 'wc-set__ok' : 'wc-set__bad'}>
      {tMail(labelKey)}: {r === null ? tMail('mail.ext.probe_skip') : r.ok ? tMail('mail.ext.probe_ok') : r.message}
    </p>
  );

  /** 포트를 고치면 보안연결이 따라간다 — 그 포트가 뜻하는 바가 정해져 있다. */
  const setPort = (which: 'recv' | 'smtp', raw: string) => {
    const port = Number(raw.replace(/[^0-9]/g, '')) || 0;
    setForm((prev) => ({
      ...prev,
      [`${which}Port`]: port,
      [`${which}Secure`]: IMPLICIT_TLS_PORTS.has(port),
    } as Form));
    if (local) setLocal(null);
  };

  const submit = () => {
    const email = form.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setLocal(tMail('mail.ext.err_email')); return; }
    if (!form.recvHost.trim() || !form.recvUser.trim()) { setLocal(tMail('mail.ext.err_host')); return; }
    // 새 계정은 비밀번호가 반드시 있어야 한다 — 고칠 때는 비워 두면 「그대로 둔다」다.
    if (!target && !form.recvPass) { setLocal(tMail('mail.ext.err_pass')); return; }

    setLocal(null);
    onSubmit({
      email,
      recvProtocol: form.recvProtocol,
      recvHost: form.recvHost.trim(),
      recvPort: form.recvPort || null,
      recvSecure: form.recvSecure,
      recvUser: form.recvUser.trim(),
      // 빈 문자열은 「그대로 둔다」 — 서버가 undefined 와 '' 를 다르게 본다.
      ...(form.recvPass ? { recvPass: form.recvPass } : {}),
      smtpHost: form.smtpHost.trim() || null,
      smtpPort: form.smtpPort || null,
      smtpSecure: form.smtpSecure,
      smtpUser: form.smtpUser.trim() || null,
      // 「같음」이면 비밀번호를 화면에서 보내지 않는다 — 고칠 때는 화면에 그 값이 아예 없다.
      ...(form.smtpSamePass
        ? { smtpSamePass: true }
        : (form.smtpPass ? { smtpPass: form.smtpPass } : {})),
      // migration 169 — IMAP 만 폴더가 있다. POP3 로 바꾼 계정에 옛 목록이 남지 않게 비운다.
      recvFolders: form.recvProtocol === 'imap' ? form.recvFolders : [],
      // migration 170 — POP3 에는 플래그가 없다. 규약을 바꾸면 켜 둔 것도 함께 끈다.
      syncReadToServer: form.recvProtocol === 'imap' && form.syncReadToServer,
      syncIntervalSec: form.syncIntervalSec,

      targetFolderId: form.targetFolderId || null,
      deleteOriginal: form.deleteOriginal,
    });
  };

  return (
    <Modal
      title={target ? tMail('mail.ext.edit_title') : tMail('mail.ext.add_title')}
      size="md"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={() => { void runProbe(); }} loading={probing} disabled={busy}>
            {tMail('mail.ext.probe')}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={busy}>{tMail('mail.ext.cancel')}</Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {target ? tMail('mail.ext.save') : tMail('mail.ext.register')}
          </Button>
        </ModalActions>
      )}
    >
      <div className="mboxmodal">
        <FormField label={tMail('mail.ext.f.email')} required>
          <Input
            wrapperClassName="wc-set__w3"
            type="email"
            value={form.email}
            maxLength={255}
            autoFocus
            placeholder="name@example.com"
            onChange={(e) => set('email', e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          />
        </FormField>

        {/* ── 받기 ── */}
        <p className="extacc__sec">{tMail('mail.ext.f.sec.recv')}</p>
        <FormField label={tMail('mail.ext.f.recv')} required>
          <Select
            className="filtrule__kind"
            ariaLabel={tMail('mail.ext.f.recv')}
            value={form.recvProtocol}
            onChange={(v) => {
              const p = v as RecvProtocol;
              setForm((prev) => ({
                ...prev, recvProtocol: p,
                recvPort: DEFAULT_RECV_PORT[p], recvSecure: true,
              }));
            }}
            options={[{ value: 'imap', label: 'IMAP' }, { value: 'pop3', label: 'POP3' }]}
          />
        </FormField>

        <div className="extacc__row">
          <FormField label={tMail('mail.ext.f.host')} required>
            <Input
              value={form.recvHost}
              maxLength={255}
              placeholder="imap.example.com"
              onChange={(e) => set('recvHost', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              fullWidth
            />
          </FormField>
          <FormField label={tMail('mail.ext.f.port')}>
            <Input
              wrapperClassName="extacc__port"
              inputMode="numeric"
              value={String(form.recvPort || '')}
              onChange={(e) => setPort('recv', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            />
          </FormField>
        </div>

        <div className="extacc__row">
          <FormField label={tMail('mail.ext.f.user')} required>
            <Input
              value={form.recvUser}
              maxLength={255}
              onChange={(e) => set('recvUser', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              fullWidth
            />
          </FormField>
          <FormField label={tMail('mail.ext.f.pass')} hint={target ? tMail('mail.ext.f.pass_keep') : undefined}>
            <PasswordInput
              value={form.recvPass}
              autoComplete="new-password"
              placeholder={target?.hasRecvPass ? SAVED_PASS_MASK : undefined}
              wrapperClassName={target?.hasRecvPass ? 'extacc__pass--saved' : undefined}
              onChange={(e) => set('recvPass', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              fullWidth
            />
          </FormField>
        </div>
        <Checkbox
          checked={form.recvSecure}
          onChange={(e) => set('recvSecure', e.target.checked)}
          label={tMail('mail.ext.f.secure')}
        />

        {/* ── 보내기 ── */}
        {/*
          ★★★ 확인 주기 (2026-09-07).
            서버는 이 값을 받고 워커가 **계정마다** 지키는데(migration 107·112), 화면에는 고칠
            자리가 없어 다들 기본 5분에 묶여 있었다 — 「저장만 되고 아무도 못 고치는 칸」이다.
          ★★ POP3 에도 듣는다. IMAP 만 되는 것들과 달리 **회사 메일에도 값이 있는** 유일한 손잡이다.
        */}
        <FormField label={tMail('mail.ext.f.interval')}>
          <Select
            value={String(form.syncIntervalSec)}
            onChange={(v) => set('syncIntervalSec', Number(v))}
            options={INTERVAL_OPTIONS.map((sec) => ({
              value: String(sec),
              label: tMail(INTERVAL_LABEL[sec]!),
            }))}
            ariaLabel={tMail('mail.ext.f.interval')}
            portal
          />
        </FormField>

        {/*
          ── 가져올 폴더 (migration 169, 2026-09-07) ──────────────────────────
          ★★★ 실측이 이 칸을 만들었다 — 사용자 폴더에 27통이 있었는데 Axi 에는 아예 없었고,
            반대로 Junk 8 · Trash 19 는 가져오면 안 되는 것들이었다.
          ★★ **저장한 뒤에** 고를 수 있다 — 목록은 서버에 실제로 붙어 봐야 안다.
          ★ POP3 에는 폴더가 없다 — 그때는 이 절 자체를 그리지 않는다.
        */}
        {form.recvProtocol === 'imap' && (
          <>
            <p className="extacc__sec">{tMail('mail.ext.f.sec.folders')}</p>
            {!target ? (
              <p className="extacc__hint">{tMail('mail.ext.f.folders_after_save')}</p>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { void loadFolders(); }}
                  disabled={foldersBusy}
                >
                  {foldersBusy ? tMail('mail.ext.f.folders_loading') : tMail('mail.ext.f.folders_load')}
                </Button>
                {foldersError && <p className="wc-set__bad">{foldersError}</p>}
                {serverFolders && serverFolders.length > 0 && (
                  <div className="extacc__folders">
                    {serverFolders.filter((f) => f.selectable).map((f) => (
                      <Checkbox
                        key={f.path}
                        checked={form.recvFolders.includes(f.path)}
                        onChange={(e) => toggleFolder(f.path, e.target.checked)}
                        label={folderLabel(f)}
                      />
                    ))}
                  </div>
                )}
                <p className="extacc__hint">{tMail('mail.ext.f.folders_hint')}</p>
              </>
            )}
            {/*
              ★★★ 켜면 우리가 **사용자의 실제 사서함을 바꾼다**(`\Seen` 을 붙인다). 지금까지
                안 하던 일이라 기본은 꺼짐이고, 끄면 무슨 일이 생기는지를 곁말로 적는다.
              ★ 「원본삭제」와 다른 스위치다 — 지우기는 되돌릴 수 없고 읽음은 되돌릴 수 있다.
            */}
            <Checkbox
              checked={form.syncReadToServer}
              onChange={(e) => set('syncReadToServer', e.target.checked)}
              label={tMail('mail.ext.f.push_seen')}
            />
            {!form.syncReadToServer && (
              <p className="extacc__hint">{tMail('mail.ext.f.push_seen_hint')}</p>
            )}
          </>
        )}

        <p className="extacc__sec">{tMail('mail.ext.f.sec.send')}</p>
        <div className="extacc__row">
          <FormField label={tMail('mail.ext.f.smtp')}>
            <Input
              value={form.smtpHost}
              maxLength={255}
              placeholder="smtp.example.com"
              onChange={(e) => set('smtpHost', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              fullWidth
            />
          </FormField>
          <FormField label={tMail('mail.ext.f.port')}>
            <Input
              wrapperClassName="extacc__port"
              inputMode="numeric"
              value={String(form.smtpPort || '')}
              onChange={(e) => setPort('smtp', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            />
          </FormField>
        </div>
        <div className="extacc__row">
          <FormField label={tMail('mail.ext.f.user')}>
            <Input
              value={form.smtpUser}
              maxLength={255}
              onChange={(e) => set('smtpUser', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              fullWidth
            />
          </FormField>
          <FormField label={tMail('mail.ext.f.pass')} hint={target ? tMail('mail.ext.f.pass_keep') : undefined}>
            <PasswordInput
              value={form.smtpSamePass ? '' : form.smtpPass}
              autoComplete="new-password"
              placeholder={target?.hasSmtpPass && !form.smtpSamePass ? SAVED_PASS_MASK : undefined}
              wrapperClassName={target?.hasSmtpPass && !form.smtpSamePass ? 'extacc__pass--saved' : undefined}
              disabled={form.smtpSamePass}
              onChange={(e) => set('smtpPass', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              fullWidth
            />
          </FormField>
        </div>
        {/*
          ★ 받기와 보내기는 표의 칸부터 갈려 있어 값이 같아도 두 번 적어야 했다 — 실제로
            보내기 칸을 비운 채 저장해 «못 보내는 계정»이 남았다(2026-09-07).
          ★★ **한 번 베끼는 것이지 계속 묶는 것이 아니다.** 그 사실을 곁말로 함께 적는다.
        */}
        <Checkbox
          checked={form.smtpSamePass}
          onChange={(e) => set('smtpSamePass', e.target.checked)}
          label={tMail('mail.ext.f.same_pass')}
        />
        {form.smtpSamePass && (
          <p className="extacc__hint">{tMail('mail.ext.f.same_pass_hint')}</p>
        )}
        <Checkbox
          checked={form.smtpSecure}
          onChange={(e) => set('smtpSecure', e.target.checked)}
          label={tMail('mail.ext.f.secure')}
        />

        {/* ── 저장 ── */}
        <p className="extacc__sec">{tMail('mail.ext.f.sec.store')}</p>
        <FormField label={tMail('mail.ext.f.folder')} hint={tMail('mail.ext.f.folder_hint')}>
          <Select
            className="wc-set__w2"
            ariaLabel={tMail('mail.ext.f.folder')}
            value={form.targetFolderId}
            onChange={(v) => set('targetFolderId', v)}
            options={[
              { value: '', label: tMail('mail.ext.inbox') },
              ...folders.map((f) => ({ value: f.id, label: f.name })),
            ]}
          />
        </FormField>
        <FormField label={tMail('mail.ext.f.delorig')}>
          <Checkbox
            checked={form.deleteOriginal}
            onChange={(e) => set('deleteOriginal', e.target.checked)}
            label={tMail('mail.ext.f.delorig_label')}
          />
          {/* 되돌릴 수 없는 것은 고르기 전에 말한다. */}
          {form.deleteOriginal && <p className="wc-set__bad">{tMail('mail.ext.f.delorig_warn')}</p>}
        </FormField>

        {probe && (
          <div className="extacc__probe" role="status">
            {probeLine('mail.ext.probe_recv', probe.recv)}
            {probeLine('mail.ext.probe_smtp', probe.smtp)}
          </div>
        )}
        {(local ?? error) && <p className="mboxmodal__err" role="alert">{local ?? error}</p>}
      </div>
    </Modal>
  );
}
